import { defineStore } from 'pinia';

import { licenseService } from '~/services/license-service';
import type { PurchaseLicenseFor, RefundLicenseResponse } from '~/types/licenses/licenses';
import { usePersonStore } from '~/stores/person';
import { memberFacingMessage } from '~/utils/errors';

/**
 * Every licence action here bills the person the screen is about. Under impersonation that is the
 * managed member, never the staffer driving the session — so the acting person is resolved from
 * the person store, and the operations the API scopes to the seat's owner take that owner's id
 * from the licence itself.
 */
export const useLicenseStore = defineStore('licenses', () => {
    // State
    const isLoading = ref(false);
    const refundData = ref<RefundLicenseResponse | null>(null);
    /**
     * The sentence a member reads about the last action that failed: the API's own words for a
     * refusal it made on purpose, ours for anything else (`memberFacingMessage`).
     */
    const error = ref<string | null>(null);
    const { showSuccess, showError, showWarning } = useGlobalToast();

    /**
     * Read the member again once an action has landed, so the screen shows what it changed.
     *
     * The action is settled before this runs, so a read that fails cannot make it a failure. It is
     * still told: the screen keeps the state from before the action, and a success beside it with
     * nothing else said invites the member to do again what has already been done. So they read
     * that the action went through and that reloading brings the page up to date.
     */
    const refreshAfter = async (landed: string): Promise<void> => {
        try {
            await usePersonStore().refreshPerson();
        } catch {
            showWarning('Page out of date', `${landed}, but we couldn't refresh this page. Reload it to see the change.`);
        }
    };

    // Actions
    /** Refund is scoped to the seat's owner — the only principal the endpoint accepts. */
    const refundLicense = async (
        ownerId: number,
        licenseId: number,
        reason: string
    ): Promise<void> => {
        refundData.value = null;
        isLoading.value = true;
        error.value = null;

        try {
            try {
                const { data } = await licenseService.refundLicense(ownerId, licenseId, { reason });
                refundData.value = data;
            } catch (err: unknown) {
                error.value = memberFacingMessage(err, 'Failed to refund license');
                // The review step presents a failed refund itself, so it is handed the failure.
                throw err;
            }

            await refreshAfter('Your refund went through');
            await useAuthStore().getMe();
        } finally {
            isLoading.value = false;
        }
    };

    /** Auto-renew is the owner's billing decision, so it is scoped to the seat's owner. */
    const updateLicenseRenewal = async (
        ownerId: number,
        licenseId: number,
        renew: boolean
    ): Promise<boolean> => {
        try {
            isLoading.value = true;
            error.value = null;

            await licenseService.updateLicense(ownerId, licenseId, { renew });

            return true;
        } catch (err: unknown) {
            const message = memberFacingMessage(err, 'Try again in a few minutes.');
            error.value = message;
            showError(`We couldn't turn ${renew ? 'on' : 'off'} auto-renew`, message);
            return false;
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Hand a seat the loaded person owns to one of their contacts. The seat holder is the person
     * on screen, so an impersonating staffer gives away the member's seat, never one of their own.
     *
     * Contact pickers distinguish a trusted person from a plain contact by sign (a contact's id is
     * carried negative to keep the two id spaces apart in one list), so the magnitude is what the
     * API is given alongside the type that disambiguates it.
     */
    const assignLicense = async (
        licenseId: number,
        contact: PurchaseLicenseFor
    ): Promise<boolean> => {
        const seatHolderId = usePersonStore().person?.id;

        if (!seatHolderId) {
            const message = 'No person loaded to assign a license from';
            error.value = message;
            showError('Error', message);
            return false;
        }

        isLoading.value = true;
        error.value = null;

        try {
            try {
                await licenseService.assignLicense(seatHolderId, licenseId, {
                    ...contact,
                    id: Math.abs(contact.id),
                });
            } catch (err: unknown) {
                const message = memberFacingMessage(err, 'Failed to assign license');
                error.value = message;
                showError('Error', message);
                return false;
            }

            showSuccess('Success', `License assigned to ${contact.name} successfully!`);

            await refreshAfter(`Your license was assigned to ${contact.name}`);
            await useAuthStore().getMe();

            return true;
        } finally {
            isLoading.value = false;
        }
    };

    /** Taking a seat back is the owner's call, so it is scoped to the seat's owner. */
    const unassignLicense = async (
        ownerId: number,
        licenseId: number
    ): Promise<boolean> => {
        isLoading.value = true;
        error.value = null;

        try {
            try {
                await licenseService.unassignLicense(ownerId, licenseId);
            } catch (err: unknown) {
                error.value = memberFacingMessage(err, 'Try again in a few minutes.');
                return false;
            }

            await refreshAfter('The license was unassigned');
            await useAuthStore().getMe();

            return true;
        } finally {
            isLoading.value = false;
        }
    };

    /** A scheduled plan change belongs to the seat's owner, so it is scoped to that owner. */
    const cancelScheduledChange = async (
        ownerId: number,
        licenseId: number
    ): Promise<boolean> => {
        isLoading.value = true;
        error.value = null;

        try {
            try {
                await licenseService.cancelScheduledChange(ownerId, licenseId);
            } catch (err: unknown) {
                error.value = memberFacingMessage(err, 'Try again in a few minutes.');
                return false;
            }

            // The licenses list drops the scheduled-change indicator once the person is read again.
            await refreshAfter('The scheduled change was canceled');

            return true;
        } finally {
            isLoading.value = false;
        }
    };

    const clearError = () => {
        error.value = null;
    };

    const resetRefundData = () => {
        refundData.value = null;
    };

    return {
        // State
        isLoading: readonly(isLoading),
        refundData: readonly(refundData),
        error: readonly(error),

        // Actions
        refundLicense,
        updateLicenseRenewal,
        clearError,
        resetRefundData,
        assignLicense,
        unassignLicense,
        cancelScheduledChange
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useLicenseStore, import.meta.hot));
}